import React, { useState, useEffect } from 'react';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { useLocation } from 'react-router-dom';
import apiClient from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import '../complaints/ComplaintManagement.css';
import RTPageLayout from '../common/RTPageLayout';

const AddPaymentMethod = ({ method, onClose, embedded = false }) => {
  const location = useLocation();
  const isEditMode = method && method.id;

  const [form, setForm] = useState({
    name: '',
    status: 'ACTIVE',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditMode) {
      setForm({
        name: method.name || '',
        status: method.status || 'ACTIVE',
      });
    } else {
      setForm({
        name: '',
        status: 'ACTIVE',
      });
    }
  }, [method, isEditMode]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) {
      newErrors.name = 'Payment method name is required';
    }
    if (!form.status) {
      newErrors.status = 'Status is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      if (isEditMode) {
        // Update existing payment method
        const response = await apiClient.post(
          `/payments/methods/${method.id}/update`,
          {
            name: form.name.trim(),
            status: form.status,
          },
        );

        toastSuccess('Payment method updated successfully');
        onClose();
      } else {
        // Create new payment method
        const response = await apiClient.post('/payments/methods', {
          name: form.name.trim(),
          status: form.status,
        });

        toastSuccess('Payment method created successfully');
        onClose();
      }
    } catch (err) {
      console.error('Submit error:', err);
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        'Failed to save payment method';
      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (embedded) {
    return (
      <div>
        <div style={{ background: 'transparent' }}>
          <h2 className="rt-title">
            {isEditMode ? 'Edit Payment Method' : 'Add Payment Method'}
          </h2>
        </div>

        <div className="rt-main-section">
          <form onSubmit={handleSubmit} className="complaint-form">
            <div className="complaint-form-sections">
              <div className="complaint-section">
                <h3 className="complaint-section-title">
                  Payment Method Details
                </h3>

                <div className="complaint-form-group">
                  <label>
                    Payment Method Name <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Enter payment method name (e.g., Cash, Bank Transfer, UPI, Card)"
                    required
                    disabled={loading}
                  />
                  {errors.name && (
                    <span className="form-error">{errors.name}</span>
                  )}
                </div>

                <div className="complaint-form-group">
                  <label>
                    Status <span style={{ color: 'red' }}>*</span>
                  </label>
                  <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                  {errors.status && (
                    <span className="form-error">{errors.status}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Loading Overlay */}
            {loading && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 9999,
                }}
              >
                <RTSpinner
                  size="medium"
                  showMessage={true}
                  message="Saving payment method..."
                />
              </div>
            )}

            <div className="complaint-actions">
              <RTButton
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </RTButton>
              <RTButton type="submit" variant="primary" disabled={loading}>
                {loading
                  ? 'Saving...'
                  : isEditMode
                    ? 'Update Payment Method'
                    : 'Add Payment Method'}
              </RTButton>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <RTPageLayout
      activePath={location.pathname}
      title={<>{isEditMode ? 'Edit Payment Method' : 'Add Payment Method'}</>}
      actions={
        <>
          <RTButton variant="secondary" onClick={onClose}>
            ← Back
          </RTButton>
        </>
      }
      mainSection={true}
    >
      <form onSubmit={handleSubmit} className="complaint-form">
        <div className="complaint-form-sections">
          <div className="complaint-section">
            <h3 className="complaint-section-title">Payment Method Details</h3>

            <div className="complaint-form-group">
              <label>
                Payment Method Name <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g., Cash, UPI, Bank Transfer, Credit Card"
                required
                disabled={loading}
              />
              {errors.name && <span className="form-error">{errors.name}</span>}
            </div>

            <div className="complaint-form-group">
              <label>
                Status <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                required
                disabled={loading}
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
              {errors.status && (
                <span className="form-error">{errors.status}</span>
              )}
            </div>
          </div>
        </div>

        {/* Loading Overlay */}
        {loading && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
            }}
          >
            <RTSpinner
              size="medium"
              showMessage={true}
              message="Saving payment method..."
            />
          </div>
        )}

        <div className="complaint-actions">
          <RTButton
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </RTButton>
          <RTButton type="submit" variant="primary" disabled={loading}>
            {loading
              ? 'Saving...'
              : isEditMode
                ? 'Update Payment Method'
                : 'Add Payment Method'}
          </RTButton>
        </div>
      </form>
    </RTPageLayout>
  );
};

export default AddPaymentMethod;
